import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";

const GAP = 16;

const questions = [
  {
    label: "What services does Yootelco offer?",
    answer:
      "We provide A2P SMS, OTP authentication, RCS messaging, voice termination, and SMS API/gateway services for enterprises and fintechs.",
  },
  {
    label: "How can I get pricing?",
    answer:
      "Pricing depends on volume and service type. Click below to chat with our team on WhatsApp for a custom quote.",
  },
  {
    label: "How do I get started?",
    answer:
      "Reach out to our team via WhatsApp or fill out our contact form, and we'll set up a quick call to understand your needs.",
  },
];

const useCookieBannerOffset = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const update = () => {
      const banner = document.querySelector(
        '[role="banner"][aria-label="Cookie consent"]'
      ) as HTMLElement | null;

      if (banner) {
        setOffset(banner.getBoundingClientRect().height + GAP);

        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(update);
          resizeObserver.observe(banner);
        }
      } else {
        setOffset(0);
        resizeObserver?.disconnect();
        resizeObserver = null;
      }
    };

    update();

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener("resize", update);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return offset;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const offset = useCookieBannerOffset();

  const activeAnswer =
    activeIndex !== null ? questions[activeIndex].answer : null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[1001] flex flex-col items-end md:bottom-6 md:right-6"
      style={
        { bottom: offset ? `${offset}px` : undefined } as React.CSSProperties
      }
    >
      {open && (
        <div className="absolute bottom-full right-0 mb-3 w-[90vw] max-w-[340px] overflow-hidden rounded-2xl border border-border/60 bg-card shadow-lift">
          <div className="flex items-center justify-between bg-primary px-4 py-3">
            <span className="text-sm font-semibold text-primary-foreground">
              Yootelco
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>

          <div className="flex max-h-[420px] flex-col gap-3 overflow-y-auto p-4">
            <div className="self-start rounded-2xl rounded-tl-none bg-secondary px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
              Hi! 👋 How can we help you today?
            </div>

            <div className="flex flex-col gap-2">
              {questions.map((q, i) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className="text-left rounded-xl border border-border/60 bg-card px-3.5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                >
                  {q.label}
                </button>
              ))}
            </div>

            {activeIndex !== null && (
              <div className="flex flex-col gap-3 animate-fade-in">
                <div className="self-start rounded-2xl rounded-tl-none bg-secondary px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
                  {activeAnswer}
                </div>

                <a
                  href="https://wa.me/33745752885?text=Hi%2C%20I%27m%20interested%20in%20Yootelco%27s%20services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white shadow-card transition-transform hover:scale-105"
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12.04 2C6.5 2 2 6.5 2 12c0 1.82.49 3.53 1.35 5.01L2 22l5.18-1.35A9.96 9.96 0 0 0 12.04 22c5.5 0 9.96-4.48 9.96-10S17.54 2 12.04 2zm4.6 14.36c-.18.5-.99.97-1.37 1.03-.36.06-.66.18-2.22-.46-1.87-.75-3.06-2.72-3.15-2.84-.09-.12-.75-.99-.75-1.88 0-.9.47-1.33.64-1.51.18-.18.39-.22.52-.22.13 0 .26.01.37.01.12 0 .28-.04.44.33.16.37.56 1.3.61 1.39.05.09.09.21.02.33-.07.12-.1.18-.21.28-.1.09-.22.21-.31.28-.1.08-.21.16-.14.32.07.16.31.51.66.83.45.4.83.53 1.01.59.18.05.28-.04.37-.12.09-.09.39-.45.49-.61.1-.16.21-.13.35-.08.14.05.9.42 1.05.5.16.08.26.12.3.19.04.07.04.4-.14.9z" />
                  </svg>
                  Chat with our team on WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lift transition-transform duration-200 hover:scale-110 md:h-16 md:w-16"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? (
          <X className="h-7 w-7 md:h-8 md:w-8" strokeWidth={2.4} />
        ) : (
          <MessageCircle
            className="h-7 w-7 md:h-8 md:w-8"
            strokeWidth={2.4}
          />
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
